import { protectedRpc } from './rpc.ts'
import type { Window } from '../lib/time.ts'

export interface SearchEntry {
  /** BARE attribute id. Sending the tree id (`8-51`) returns nothing useful. */
  attribute: string
  att_operator?: string | null
  att_value?: string | null
  filter?: string | null
  filter_value?: string | null
  period?: string | null
}

export interface SearchReply {
  header?: unknown[][]
  items?: unknown[][]
}

/** A fetched table: column names from `header[i][0]`, rows as raw strings. */
export interface Table {
  columns: string[]
  rows: string[][]
  /** Pages fetched and whether the page-count guard tripped. */
  pages: number
  truncated: boolean
}

const PAGE_LIMIT = 5000
const MAX_PAGES = 2000

/**
 * Fetch every row for one entry.
 *
 * Three things make this loop non-obvious:
 *  - `limit` is clamped silently, so `got.length < limit` is indistinguishable
 *    from "last page" — ONLY an empty page proves the end;
 *  - rows come back NEWEST FIRST, so one clamped page of a week-long window is
 *    the most recent day and nothing else;
 *  - `offset` advances by rows actually returned, never by `limit`.
 */
export async function searchAll(
  interfaceId: number,
  window: Window,
  entry: SearchEntry,
  onProgress?: (rows: number, page: number) => void,
): Promise<Table> {
  let columns: string[] = []
  const rows: string[][] = []
  let offset = 0
  let prevFirst = ''
  let page = 0
  let truncated = false

  for (; page < MAX_PAGES; page++) {
    const reply = await protectedRpc<SearchReply>('log_data.log_search', {
      interface_id: interfaceId,
      from_date: window.from,
      to_date: window.to,
      entries: [
        {
          attribute: entry.attribute,
          att_operator: entry.att_operator ?? null,
          att_value: entry.att_value ?? null,
          filter: entry.filter ?? null,
          filter_value: entry.filter_value ?? null,
          period: entry.period ?? null,
        },
      ],
      limit: PAGE_LIMIT,
      offset,
    })

    const got = (reply?.items ?? []) as unknown[][]
    if (!columns.length && Array.isArray(reply?.header)) {
      columns = reply.header.map((h) => String((Array.isArray(h) ? h[0] : h) ?? ''))
    }
    if (!got.length) break // only an empty page proves the end

    const first = JSON.stringify(got[0])
    if (first === prevFirst) break // a server ignoring `offset`
    prevFirst = first

    for (const r of got) rows.push((r as unknown[]).map((c) => (c == null ? '' : String(c))))
    offset += got.length // advance by what ARRIVED, not by `limit`
    onProgress?.(rows.length, page + 1)
  }
  if (page >= MAX_PAGES) truncated = true

  return { columns, rows, pages: page, truncated }
}

/** Case-insensitive column index by machine name, or -1. */
export function columnIndex(table: Table, machineName: string): number {
  const want = machineName.toLowerCase()
  return table.columns.findIndex((c) => c.toLowerCase() === want)
}

/**
 * A reply is the whole record, not one column: two different attributes can
 * answer with the identical table. Keying on the request de-duplicates the
 * fetch so a five-attribute report doesn't carry the same megabytes five times.
 */
export function entryKey(interfaceId: number, window: Window, entry: SearchEntry): string {
  return JSON.stringify([
    interfaceId,
    window.from,
    window.to,
    entry.attribute,
    entry.att_operator ?? null,
    entry.att_value ?? null,
    entry.filter ?? null,
    entry.filter_value ?? null,
    entry.period ?? null,
  ])
}

export function createSearchCache() {
  const cache = new Map<string, Promise<Table>>()
  return {
    fetch(
      interfaceId: number,
      window: Window,
      entry: SearchEntry,
      onProgress?: (rows: number, page: number) => void,
    ): Promise<Table> {
      const key = entryKey(interfaceId, window, entry)
      let hit = cache.get(key)
      if (!hit) {
        hit = searchAll(interfaceId, window, entry, onProgress)
        cache.set(key, hit)
      }
      return hit
    },
    clear() {
      cache.clear()
    },
    get size() {
      return cache.size
    },
  }
}

/** `"676088672 / 2"` → `{ addr, index }`. One-output decoders arrive bare. */
export function splitIoAddr(value: string): { addr: string; index: string | null } {
  const parts = String(value).split(/[/·]/).map((p) => p.trim()).filter(Boolean)
  if (parts.length >= 2) return { addr: parts[0]!, index: parts[1]! }
  return { addr: parts[0] ?? '', index: null }
}

/** Values arrive as strings; anything unparseable is null, never 0. */
export function num(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(String(value).trim().replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
